import { CSSProperties, FunctionComponent, useState } from 'react'
import { Colors, FontSizeRem } from '@kvika/audur-theme'
import { CircleInfo, Close } from '@kvika/audur-icons'
import { PrismicRichText } from '@prismicio/react'
import { RichTextField } from '@prismicio/client'

import {
	Backdrop,
	StyledButton,
	StyledCloseButton,
	StyledContainer,
	StyledFontWrapper,
	StyledHeading,
	StyledTitleRow,
	StyledTooltip,
} from './TooltipStyles'

type TooltipPlacement =
	| 'top'
	| 'top-start'
	| 'top-end'
	| 'bottom'
	| 'bottom-start'
	| 'bottom-end'
	| 'left'
	| 'left-start'
	| 'left-end'
	| 'right'
	| 'right-start'
	| 'right-end'

type TooltipProps = {
	text: string | RichTextField
	heading?: string
	textColor?: string
	backgroundColor?: string
	fontSize?: string
	showArrow?: boolean
	placement?: TooltipPlacement
	maxWidth?: string
	infoOutlinedColor?: string
	hideCloseIcon?: boolean
	hideTitleRow?: boolean
	activeInfoOutlinedColor?: string
	id?: string
}

type TooltipContentProps = {
	text: string | RichTextField
	heading?: string
	textColor?: string
	fontSize?: string
	maxWidth?: string
	hideCloseIcon?: boolean
	hideTitleRow?: boolean
	onClose(): void
}

const TooltipContent: FunctionComponent<TooltipContentProps> = ({
	text,
	heading,
	textColor,
	fontSize,
	maxWidth,
	hideCloseIcon,
	hideTitleRow,
	onClose,
}) => {
	const textStyle: CSSProperties = {
		flex: 1,
		marginTop: 0,
		whiteSpace: 'pre-wrap',
		fontFamily: 'CircularXX',
		color: textColor ?? Colors.DarkGray,
		fontSize: fontSize ?? FontSizeRem.Body,
	}

	return (
		<StyledFontWrapper>
			<StyledContainer style={{ maxWidth: maxWidth ?? '300px' }}>
				{!hideTitleRow && (
					<StyledTitleRow>
						<StyledHeading>{heading}</StyledHeading>
						{!hideCloseIcon && (
							<StyledCloseButton onClick={onClose} aria-label='Close'>
								<Close color={Colors.DarkGray} />
							</StyledCloseButton>
						)}
					</StyledTitleRow>
				)}
				{typeof text === 'string' ? (
					<p style={textStyle}>{text}</p>
				) : (
					<PrismicRichText
						field={text}
						components={{
							paragraph: ({ children }) => <p style={textStyle}>{children}</p>,
							hyperlink: ({ children, node }) => {
								const { url } = node.data
								return (
									<a
										href={url}
										target={'target' in node.data && node.data.target ? node.data.target : '_blank'}
										rel='noopener noreferrer'
										style={{ color: '#3B33BC', textDecoration: 'underline' }}
									>
										{children}
									</a>
								)
							},
						}}
					/>
				)}
			</StyledContainer>
		</StyledFontWrapper>
	)
}

const AudurTooltip: FunctionComponent<TooltipProps> = ({
	text,
	heading,
	textColor,
	backgroundColor,
	fontSize,
	placement,
	maxWidth,
	infoOutlinedColor,
	hideCloseIcon,
	hideTitleRow,
	activeInfoOutlinedColor,
	id,
}) => {
	const [isOpen, setIsOpen] = useState(false)

	const handleClose = () => setIsOpen(false)

	const iconColor =
		(activeInfoOutlinedColor && isOpen ? activeInfoOutlinedColor : infoOutlinedColor) ?? Colors.LightOrange

	return (
		<>
			{isOpen && <Backdrop onClick={handleClose} />}
			<StyledTooltip
				id={id ?? 'audur-tooltip'}
				content={
					<TooltipContent
						text={text}
						heading={heading}
						textColor={textColor}
						fontSize={fontSize}
						maxWidth={maxWidth}
						hideCloseIcon={hideCloseIcon}
						hideTitleRow={hideTitleRow}
						onClose={handleClose}
					/>
				}
				toggledOpen={isOpen}
				placement={placement ?? 'bottom-end'}
				fontFamily='var(--CircularXX), Arial, sans-serif'
				borderColor={backgroundColor}
			>
				<StyledButton onClick={() => setIsOpen((prev) => !prev)} style={{ zIndex: isOpen ? 999 : 1 }}>
					<CircleInfo color={iconColor} />
				</StyledButton>
			</StyledTooltip>
		</>
	)
}

export default AudurTooltip
